import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import { MultipleOrigins } from 'models/MultipleOrigins/multipleOrigins.model';
import { ExpandIconState } from 'shared/components/RawMaterialMapColumnCard';
import { DEFAULT_NODE_HEIGHT } from '.';
import type { NodeLabels } from './productFlowMap.types';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';

//Function to get the node's individual height
export const getHeightById = (id: string, isDownload?: boolean): number => {
  if (isDownload) {
    const element = document.getElementById(id);
    if (!element) return 0;
    return element.offsetHeight;
  } else return DEFAULT_NODE_HEIGHT;
};

const legTypeToRemoveError = 'legTypeToRemoveError';

//Function to return nodeComponents based on the stageEmissions value
export const getNodeCards = (props: {
  emissionType: DataMappingProps;
  emissions: ProductRawMaterial[] | ProductRawMaterialTransportations[] | any;
  isDownload?: boolean;
  hideColor?: boolean;
}): NodeLabels[] => {
  const { emissionType, emissions, isDownload, hideColor } = props;
  switch (emissionType) {
    case DataMappingProps.RAW_MATERIAL: {
      if (!emissions?.length) return [];
      const val = emissions?.map((item: ProductRawMaterial, index: number) => {
        const parentId = `${DataMappingProps.RAW_MATERIAL}-${index + 1}-parent`;
        const parentObject =
          item.multipleOrigins?.length && item.multipleOrigins?.length > 1
            ? [
                {
                  id: `parent ${item.multipleOrigins[0].id}`,
                  label: {
                    name: item.activityLabel,
                    emission: hideColor
                      ? -1
                      : item.sumOfNormalisedCo2e !== null ||
                        item.sumOfNormalisedCo2e !== undefined
                      ? item.sumOfNormalisedCo2e
                      : -1,
                    carbon: item.sumOfCo2e,
                    weight: item.totalQuantity,
                    unit: item.unit,
                    id: parentId,
                    expandIcon: isDownload
                      ? ExpandIconState.OPEN
                      : ExpandIconState.CLOSED,
                    isOrigin: false,
                    isTop: true,
                    isDownloadPage: isDownload,
                  },
                  height: getHeightById(parentId, isDownload),
                  isSubNode: false,
                  isSubNodesVisible: !!isDownload,
                  hasSubNodes: true,
                },
              ]
            : [];
        const col1NodeProps = item.multipleOrigins?.map(
          (origin: MultipleOrigins, originIndex) => {
            const nodeId = `${DataMappingProps.RAW_MATERIAL}-${index + 1}-${
              originIndex + 1
            }`;
            const isOrigin =
              !!parentObject.length ??
              (item.multipleOrigins?.length &&
                item.multipleOrigins?.length > 1 &&
                originIndex > 0);
            const dataColumnCardProps = {
              name: isOrigin ? origin.origin : item.activityLabel,
              emission: hideColor
                ? -1
                : origin.normalisedCo2e !== null ||
                  origin.normalisedCo2e !== undefined
                ? origin.normalisedCo2e
                : -1,
              carbon: origin.co2e,
              weight: origin.weight,
              unit: origin.unit,
              id: nodeId,
              isBottom:
                item.multipleOrigins?.length &&
                item.multipleOrigins?.length > 1 &&
                originIndex + 1 === item.multipleOrigins?.length,
              expandIcon: !!parentObject.length
                ? undefined
                : item.multipleOrigins?.length &&
                  item.multipleOrigins?.length > 1 &&
                  originIndex === 0
                ? ExpandIconState.CLOSED
                : undefined,
              isOrigin: !!isOrigin,
              isDownloadPage: isDownload,
            };
            return {
              id: `${origin.id}-${originIndex + 1}`,
              label: dataColumnCardProps,
              height: getHeightById(nodeId, isDownload),
              isSubNode: !!isOrigin,
              isParentExpanded: !!isDownload,
              isSubNodesVisible:
                item?.multipleOrigins?.length &&
                item.multipleOrigins.length > 1 &&
                !isOrigin,
              parentId:
                isOrigin &&
                item.multipleOrigins?.length &&
                `parent ${item.multipleOrigins[0].id}`,
            };
          }
        );
        return col1NodeProps?.length ? [...parentObject, ...col1NodeProps] : [];
      });
      return val.flat();
    }
    case DataMappingProps.TRANSPORTATION: {
      if (!emissions?.length) return [];
      const val = emissions?.map(
        (item: ProductRawMaterialTransportations, index: number) => {
          const parentId = `${DataMappingProps.TRANSPORTATION}-${
            index + 1
          }-parent`;
          const parentObject =
            item.multipleOrigins?.length && item.multipleOrigins?.length > 1
              ? [
                  {
                    id: `parent ${item.multipleOrigins[0].id}`,
                    label: {
                      name: item.temperatureControl,
                      emission: hideColor
                        ? -1
                        : item.sumOfNormalisedCo2e !== null ||
                          item.sumOfNormalisedCo2e !== undefined
                        ? item.sumOfNormalisedCo2e
                        : -1,
                      carbon: item.sumOfCo2e,
                      activityLabel: item.activityLabel ?? '',
                      id: parentId,
                      isOrigin: false,
                      expandIcon: isDownload
                        ? ExpandIconState.OPEN
                        : ExpandIconState.CLOSED,
                      isTop: true,
                      isDownloadPage: isDownload,
                      originToSupplierLegType: item.originToSupplierLegType
                        ? legTypeToRemoveError
                        : '',
                      supplierToPfLegType: item.supplierToPfLegType
                        ? legTypeToRemoveError
                        : '',
                    },
                    height: getHeightById(parentId, isDownload),
                    isSubNode: false,
                    isSubNodesVisible: !!isDownload,
                    hasSubNodes: true,
                  },
                ]
              : [];
          const col2NodeProps = item.multipleOrigins?.map(
            (origin: MultipleOrigins, originIndex) => {
              const nodeId = `${DataMappingProps.TRANSPORTATION}-${index + 1}-${
                originIndex + 1
              }`;
              const isOrigin =
                !!parentObject.length ??
                (item.multipleOrigins?.length &&
                  item.multipleOrigins?.length > 1 &&
                  originIndex > 0);

              const transportationColumnCardProps = {
                name: isOrigin ? origin.origin : item.temperatureControl,
                emission: hideColor
                  ? -1
                  : origin.normalisedCo2e !== null ||
                    origin.normalisedCo2e !== undefined
                  ? origin.normalisedCo2e
                  : -1,
                carbon: origin.co2e,
                legType: origin.legType ?? '',
                activityLabel: isOrigin
                  ? origin.origin
                  : item.activityLabel ?? '',
                originToSupplierLegType: origin.originToSupplierLegType ?? '',
                supplierToPfLegType: origin.supplierToPfLegType ?? '',
                id: nodeId,
                isOrigin: !!isOrigin,
                isBottom:
                  item.multipleOrigins?.length &&
                  item.multipleOrigins?.length > 1 &&
                  originIndex + 1 === item.multipleOrigins?.length,
                expandIcon: !!parentObject.length
                  ? undefined
                  : item.multipleOrigins?.length &&
                    item.multipleOrigins?.length > 1 &&
                    originIndex === 0
                  ? ExpandIconState.CLOSED
                  : undefined,
                isDownloadPage: isDownload,
              };
              return {
                id: `${origin.id}-${originIndex + 1}`,
                label: transportationColumnCardProps,
                height: getHeightById(nodeId, isDownload),
                isSubNode: !!isOrigin,
                isParentExpanded: !!isDownload,
                isSubNodesVisible:
                  item?.multipleOrigins?.length &&
                  item.multipleOrigins.length > 1 &&
                  !isOrigin,
                parentId:
                  isOrigin &&
                  item.multipleOrigins?.length &&
                  `parent ${item.multipleOrigins[0].id}`,
              };
            }
          );
          return col2NodeProps?.length
            ? [...parentObject, ...col2NodeProps]
            : [];
        }
      );
      return val.flat();
    }
    case DataMappingProps.PROCESSING_FACILITY: {
      if (!emissions || !emissions.processingFacilityName) return [];
      const nodeId = `${DataMappingProps.PROCESSING_FACILITY}-1`;
      const pfColumnCardProps = {
        name: emissions.processingFacilityName,
        emission: hideColor
          ? -1
          : emissions.normalised_co2e !== null ||
            emissions.normalised_co2e !== undefined
          ? emissions.normalised_co2e
          : -1,
        carbon: emissions.co2e,
        id: nodeId,
        isDownloadPage: isDownload,
      };
      return [
        {
          label: pfColumnCardProps,
          height: getHeightById(nodeId, isDownload),
          id: Number(emissions.productId),
        },
      ];
    }
    case DataMappingProps.PRODUCT_DISTRIBUTION: {
      if (!emissions?.length) return [];
      console.log('emissions ', emissions);
      return emissions?.map(
        (item: ProductDistributionEmissionData, index: number) => {
          const nodeId = `${DataMappingProps.PRODUCT_DISTRIBUTION}-${
            index + 1
          }`;
          const pdColumnCardProps = {
            name: item.endMarketLocation,
            emission: hideColor
              ? -1
              : item.normalisedCo2e !== null ||
                item.normalisedCo2e !== undefined
              ? item.normalisedCo2e
              : -1,
            carbon: item.co2e,
            legType: item.legType ?? '',
            id: nodeId,
            isDownloadPage: isDownload,
          };
          return {
            label: pdColumnCardProps,
            height: getHeightById(nodeId, isDownload),
            id: item.id,
          };
        }
      );
    }
    default:
      return [];
  }
};
